import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LabelOffIcon from '@mui/icons-material/LabelOff';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PaletteIcon from '@mui/icons-material/Palette';
import SellIcon from '@mui/icons-material/Sell';
import { BaseDialog } from '@modules/ui-components';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { StrategyBlock } from '@segmently/schema-models/strategy';

const BLOCK_TAG_PREFIX = 'block:';
const STRATEGY_TAG_PREFIX = 'strategy:';

export interface AssignBlockTagPayload {
  blockId: string;
  strategyId?: string;
  blockDocId?: string;
}

const getTags = (step: FlowStepNode): string[] => (
  Array.isArray(step.tags)
    ? step.tags.filter((tag): tag is string => typeof tag === 'string')
    : []
);

const getPrefixedTags = (step: FlowStepNode, prefix: string): string[] => (
  getTags(step).filter((tag) => tag.startsWith(prefix))
);

const stripPrefix = (tag: string, prefix: string): string => tag.slice(prefix.length);

const normalizeBlockTagInput = (value: string): string => value.trim().replace(/^block:/, '');

const getSelectableScreenIds = (screen: FlowStepNode): string[] => (
  Array.from(new Set([screen.entityId, screen.id].filter((id): id is string => typeof id === 'string' && id.length > 0)))
);

const getBlockIdentity = (block: StrategyBlock): string => block.entityId || block.id;

const getBlockLabel = (block: StrategyBlock): string => {
  const id = getBlockIdentity(block);
  const title = block.name || block.blockType || id;
  return title === id ? id : `${title} (${id})`;
};

interface SelectionState {
  blockLabel: string;
  strategyLabel: string;
  canRemove: boolean;
  selectedStrategyId: string | null;
}

interface OnboardingBlockTagControlsProps {
  projectId: string;
  selectedScreenIds: string[];
  screens: FlowStepNode[];
  disabled?: boolean;
  /**
   * When false, only the theme-apply items are shown (Update UI from theme,
   * Edit theme & apply). Block/strategy management items are admin-only.
   */
  isAdmin?: boolean;
  associatedStrategyId?: string | null;
  associatedStrategyName?: string;
  onAssignBlockTag: (payload: AssignBlockTagPayload) => Promise<void>;
  onRemoveBlockTags: () => Promise<void>;
  onRequestAssociateStrategy?: () => void;
  onOpenAssociatedStrategy?: () => void;
  /** Opens the selective theme apply dialog for the selected screens */
  onUpdateUiFromTheme?: () => void;
  /** Opens the edit-theme-and-apply dialog for the selected screens */
  onEditAndApplyTheme?: () => void;
  /** Disables "Update UI from theme" (e.g. no saved theme settings) */
  updateUiFromThemeDisabled?: boolean;
  /** Tooltip reason shown when the item is disabled */
  updateUiFromThemeDisabledReason?: string;
}

export const OnboardingBlockTagControls: React.FC<OnboardingBlockTagControlsProps> = ({
  projectId,
  selectedScreenIds,
  screens,
  disabled = false,
  isAdmin = false,
  associatedStrategyId,
  associatedStrategyName,
  onAssignBlockTag,
  onRemoveBlockTags,
  onRequestAssociateStrategy,
  onOpenAssociatedStrategy,
  onUpdateUiFromTheme,
  onEditAndApplyTheme,
  updateUiFromThemeDisabled = false,
  updateUiFromThemeDisabledReason,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [manualBlockId, setManualBlockId] = useState('');
  const [selectedBlock, setSelectedBlock] = useState<StrategyBlock | null>(null);
  const [strategyBlocks, setStrategyBlocks] = useState<StrategyBlock[]>([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedScreens = useMemo(() => {
    const selected = new Set(selectedScreenIds);
    return screens.filter((screen) => getSelectableScreenIds(screen).some(id => selected.has(id)));
  }, [screens, selectedScreenIds]);

  const existingBlockIds = useMemo(() => {
    const ids = new Set<string>();
    screens.forEach((screen) => {
      getPrefixedTags(screen, BLOCK_TAG_PREFIX).forEach((tag) => ids.add(stripPrefix(tag, BLOCK_TAG_PREFIX)));
    });
    return Array.from(ids).sort((a, b) => a.localeCompare(b));
  }, [screens]);

  const selectionState = useMemo<SelectionState>(() => {
    if (selectedScreens.length === 0) {
      return {
        blockLabel: '',
        strategyLabel: '',
        canRemove: false,
        selectedStrategyId: null,
      };
    }

    const selectedBlockTagSets = selectedScreens.map((screen) => getPrefixedTags(screen, BLOCK_TAG_PREFIX));
    const selectedBlockIds = new Set(selectedBlockTagSets.flat().map((tag) => stripPrefix(tag, BLOCK_TAG_PREFIX)));
    const hasAnyBlock = selectedBlockIds.size > 0;
    const commonBlockId = selectedBlockIds.size === 1 ? Array.from(selectedBlockIds)[0] : null;
    const everyScreenHasSameSingleBlock = Boolean(commonBlockId)
      && selectedBlockTagSets.every((tags) => tags.length === 1 && stripPrefix(tags[0], BLOCK_TAG_PREFIX) === commonBlockId);

    const selectedStrategyIds = new Set(
      selectedScreens
        .flatMap((screen) => getPrefixedTags(screen, STRATEGY_TAG_PREFIX))
        .map((tag) => stripPrefix(tag, STRATEGY_TAG_PREFIX))
        .filter(Boolean),
    );
    const commonStrategyId = selectedStrategyIds.size === 1 ? Array.from(selectedStrategyIds)[0] : null;

    const blockLabel = !hasAnyBlock
      ? (selectedScreens.length === 1 ? 'No block label' : `${selectedScreens.length} selected: no block label`)
      : everyScreenHasSameSingleBlock && commonBlockId
        ? `Block: ${commonBlockId}`
        : selectedScreens.length === 1
          ? `Blocks: ${Array.from(selectedBlockIds).join(', ')}`
          : `${selectedScreens.length} selected: mixed block labels`;

    const effectiveStrategyLabel = associatedStrategyName
      ? `${associatedStrategyName}${associatedStrategyId ? ` (${associatedStrategyId})` : ''}`
      : associatedStrategyId || commonStrategyId;

    const strategyLabel = selectedStrategyIds.size > 1
      ? 'Mixed strategy tags'
      : effectiveStrategyLabel
        ? `Strategy: ${effectiveStrategyLabel}`
        : 'No associated strategy';

    return {
      blockLabel,
      strategyLabel,
      canRemove: hasAnyBlock,
      selectedStrategyId: commonStrategyId,
    };
  }, [associatedStrategyId, associatedStrategyName, selectedScreens]);

  const effectiveStrategyId = associatedStrategyId || selectionState.selectedStrategyId;
  const hasAssociatedStrategy = Boolean(effectiveStrategyId);

  useEffect(() => {
    if (!assignOpen) return;
    setManualBlockId(existingBlockIds[0] ?? '');
    setSelectedBlock(null);
  }, [assignOpen, existingBlockIds]);

  useEffect(() => {
    if (!assignOpen || !effectiveStrategyId || !projectId) {
      setStrategyBlocks([]);
      setBlocksLoading(false);
      return;
    }

    let isActive = true;
    setBlocksLoading(true);

    new StrategyBlockService(projectId, effectiveStrategyId)
      .list({ limit: 250 })
      .then((blocks) => {
        if (!isActive) return;
        const sorted = [...blocks].sort((a, b) => {
          const aOrder = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
          const bOrder = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return getBlockLabel(a).localeCompare(getBlockLabel(b));
        });
        setStrategyBlocks(sorted);
      })
      .catch((err) => {
        console.warn('[OnboardingBlockTagControls] Failed to load strategy blocks:', err);
        if (isActive) setStrategyBlocks([]);
      })
      .finally(() => {
        if (isActive) setBlocksLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [assignOpen, effectiveStrategyId, projectId]);

  const closeMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const closeAssign = useCallback(() => {
    if (saving) return;
    setAssignOpen(false);
  }, [saving]);

  const handleOpenAssign = useCallback(() => {
    closeMenu();
    setAssignOpen(true);
  }, [closeMenu]);

  const handleRequestAssociateStrategy = useCallback(() => {
    closeMenu();
    onRequestAssociateStrategy?.();
  }, [closeMenu, onRequestAssociateStrategy]);

  const handleOpenAssociatedStrategy = useCallback(() => {
    closeMenu();
    onOpenAssociatedStrategy?.();
  }, [closeMenu, onOpenAssociatedStrategy]);

  const handleAssign = useCallback(async () => {
    const blockId = selectedBlock ? getBlockIdentity(selectedBlock) : normalizeBlockTagInput(manualBlockId);
    if (!blockId || saving) return;

    setSaving(true);
    try {
      await onAssignBlockTag({
        blockId,
        strategyId: effectiveStrategyId || undefined,
        blockDocId: selectedBlock?.id,
      });
      setAssignOpen(false);
    } finally {
      setSaving(false);
    }
  }, [effectiveStrategyId, manualBlockId, onAssignBlockTag, saving, selectedBlock]);

  const handleRemove = useCallback(async () => {
    if (saving) return;
    closeMenu();
    setSaving(true);
    try {
      await onRemoveBlockTags();
    } finally {
      setSaving(false);
    }
  }, [closeMenu, onRemoveBlockTags, saving]);

  if (selectedScreens.length === 0) return null;

  const menuOpen = Boolean(menuAnchor);
  const assignDisabled = disabled || saving;
  const removeDisabled = disabled || saving || !selectionState.canRemove;
  const selectedBlockIdentity = selectedBlock ? getBlockIdentity(selectedBlock) : normalizeBlockTagInput(manualBlockId);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        endIcon={<ExpandMoreIcon />}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        disabled={disabled || saving}
        data-testid="onboarding-block-tag-controls"
        sx={{
          minWidth: 'auto',
          px: 1.5,
          py: 0.5,
          fontSize: '0.8125rem',
          textTransform: 'none',
        }}
      >
        Bulk Actions
      </Button>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        data-testid="onboarding-block-tag-menu"
      >
        <Box sx={{ px: 2, py: 1.25, maxWidth: 360 }}>
          <Typography variant="caption" color="text.secondary">
            {selectedScreens.length} selected
          </Typography>
          {isAdmin && (
            <>
              <Typography variant="body2" data-testid="onboarding-block-tag-summary" sx={{ mt: 0.25 }}>
                {selectionState.blockLabel}
              </Typography>
              <Typography variant="body2" color={hasAssociatedStrategy ? 'text.primary' : 'text.secondary'} sx={{ mt: 0.5 }}>
                {selectionState.strategyLabel}
              </Typography>
            </>
          )}
        </Box>
        <Divider />
        {isAdmin && (
          <MenuItem onClick={handleOpenAssign} disabled={assignDisabled} data-testid="onboarding-assign-block-tag-button">
            <SellIcon sx={{ mr: 1, fontSize: 20 }} />
            Assign Block
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem onClick={handleRemove} disabled={removeDisabled} data-testid="onboarding-remove-block-tag-button">
            <LabelOffIcon sx={{ mr: 1, fontSize: 20 }} />
            Remove Block Label
          </MenuItem>
        )}
        {isAdmin && <Divider />}
        <Tooltip
          title={updateUiFromThemeDisabled && updateUiFromThemeDisabledReason ? updateUiFromThemeDisabledReason : ''}
          placement="right"
        >
          <span>
            <MenuItem
              onClick={() => {
                closeMenu();
                onUpdateUiFromTheme?.();
              }}
              disabled={!onUpdateUiFromTheme || updateUiFromThemeDisabled || selectedScreens.length === 0}
              data-testid="onboarding-update-ui-from-theme-button"
            >
              <PaletteIcon sx={{ mr: 1, fontSize: 20 }} />
              Update UI from theme
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem
          onClick={() => {
            closeMenu();
            onEditAndApplyTheme?.();
          }}
          disabled={!onEditAndApplyTheme || selectedScreens.length === 0}
          data-testid="onboarding-edit-apply-theme-button"
        >
          <PaletteIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit theme &amp; apply…
        </MenuItem>
        {isAdmin && <Divider />}
        {isAdmin && (
          <MenuItem onClick={handleRequestAssociateStrategy} disabled={!onRequestAssociateStrategy} data-testid="onboarding-associate-strategy-button">
            <AccountTreeIcon sx={{ mr: 1, fontSize: 20 }} />
            Associate Strategy
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem
            onClick={handleOpenAssociatedStrategy}
            disabled={!hasAssociatedStrategy || !onOpenAssociatedStrategy}
            data-testid="onboarding-open-associated-strategy-button"
          >
            <OpenInFullIcon sx={{ mr: 1, fontSize: 20 }} />
            Open Associated Strategy
          </MenuItem>
        )}
      </Menu>

      <BaseDialog
        open={assignOpen}
        onClose={closeAssign}
        maxWidth="sm"
        fullWidth
        mode="modal"
        data-testid="onboarding-block-tag-dialog"
        aria-labelledby="onboarding-block-tag-dialog-title"
      >
        <DialogTitle
          id="onboarding-block-tag-dialog-title"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <Typography variant="h6">Assign Block</Typography>
          <IconButton aria-label="close assign block tag" size="small" onClick={closeAssign} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Selected screens keep source tags. Assigning a strategy block replaces only the active block label.
            </Typography>

            {hasAssociatedStrategy ? (
              <>
                <Chip
                  size="small"
                  icon={<AccountTreeIcon />}
                  label={associatedStrategyName || effectiveStrategyId}
                  sx={{ alignSelf: 'flex-start' }}
                />
                <Autocomplete
                  options={strategyBlocks}
                  loading={blocksLoading}
                  value={selectedBlock}
                  onChange={(_, value) => {
                    setSelectedBlock(value);
                    setManualBlockId(value ? getBlockIdentity(value) : '');
                  }}
                  getOptionLabel={getBlockLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options, state) => {
                    const query = state.inputValue.trim().toLowerCase();
                    if (!query) return options;
                    return options.filter((block) => (
                      getBlockLabel(block).toLowerCase().includes(query)
                      || getBlockIdentity(block).toLowerCase().includes(query)
                      || block.id.toLowerCase().includes(query)
                    ));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Strategy block"
                      placeholder={blocksLoading ? 'Loading blocks...' : 'Search blocks'}
                      size="small"
                      inputProps={{
                        ...params.inputProps,
                        'data-testid': 'onboarding-block-tag-input',
                      }}
                    />
                  )}
                />
              </>
            ) : (
              <>
                {existingBlockIds.length > 0 && (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {existingBlockIds.map((id) => (
                      <Chip
                        key={id}
                        size="small"
                        label={id}
                        onClick={() => {
                          setSelectedBlock(null);
                          setManualBlockId(id);
                        }}
                        variant={manualBlockId === id ? 'filled' : 'outlined'}
                        data-testid={`onboarding-block-tag-option-${id}`}
                      />
                    ))}
                  </Stack>
                )}

                <TextField
                  label="Block label"
                  value={manualBlockId}
                  onChange={(event) => {
                    setSelectedBlock(null);
                    setManualBlockId(event.target.value);
                  }}
                  placeholder="welcome_trust"
                  size="small"
                  fullWidth
                  disabled={saving}
                  inputProps={{ 'data-testid': 'onboarding-block-tag-input' }}
                />
              </>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">
                Saved as {effectiveStrategyId ? `strategy:${effectiveStrategyId}, ` : ''}block:{selectedBlockIdentity || '<id>'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAssign} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={saving || !selectedBlockIdentity}
            data-testid="onboarding-block-tag-save"
          >
            {saving ? 'Saving...' : 'Assign'}
          </Button>
        </DialogActions>
      </BaseDialog>
    </>
  );
};
